import queryString from 'query-string-for-all';
import loadable from '@loadable/component';
import productCollectionPageQuery from './query.graphql';

const ProductCollection = loadable(() =>
  import(/* webpackChunkName: "product-collection" */ './product-collection')
);

ProductCollection.graphQL = productCollectionPageQuery;

ProductCollection.loadOrder = [{ main: true }];

const mergeIntoQueryArray = (arr, obj, prop) => {
  const currentObj = arr.find(item => item[prop] === obj[prop]);
  if (currentObj) {
    return arr.map(item =>
      item[prop] === obj[prop]
        ? {
            ...item,
            ...obj,
          }
        : item
    );
  }
  return arr.concat(obj);
};

const replaceOrAdd = (array, entry, matchBy = 'property') => {
  const index = array.findIndex(x => x[matchBy] === entry[matchBy]);
  const newArr = [...array];
  if (index >= 0) {
    newArr.splice(index, 1, entry);
  } else {
    newArr.push(entry);
  }

  return newArr;
};

ProductCollection.dataReducer = (_, newState) => newState;

ProductCollection.reducer = (state = {}, action) => {
  switch (action.type) {
    case 'SSR':
    case 'URL_CHANGE': {
      const { offset } = queryString.parse(action.location.search);

      return {
        ...state,
        offset: parseInt(offset, 10) || 0,
        // Use url to construct new filters server side
        url: action.location.pathname + action.location.search,
        filters: [],
      };
    }
    case 'ROUTE_LOADED': {
      return {
        ...state,
        // Clear url and use local filters instead
        url: null,
        filters: action.data && action.data.productCollection.filters,
      };
    }
    case 'FILTER_RANGE_UPDATE': {
      return {
        ...state,
        filters: replaceOrAdd(state.filters, action.filter),
      };
    }
    case 'FILTER_LIST_UPDATE': {
      const currentFilter = state.filters.find(
        x => x.property === action.property
      );

      const filter = {
        type: 'terms',
        ids: currentFilter && currentFilter.ids ? [...currentFilter.ids] : [],
        property: action.property,
      };

      const { id } = action;

      if (!id) {
        return state;
      }

      if (!action.multiple) {
        const newFilters = replaceOrAdd(state.filters, {
          type: 'term',
          id,
          property: action.property,
        });

        return {
          ...state,
          filters: newFilters.filter(
            item => item.id || (item.ids && item.ids.length)
          ),
        };
      }

      // Convert to terms filter
      if (
        currentFilter &&
        currentFilter.type === 'term' &&
        currentFilter.id !== id
      ) {
        filter.ids.push(currentFilter.id);
      }

      const index = filter.ids.indexOf(id);

      if (index >= 0) {
        filter.ids.splice(index, 1);
      } else {
        filter.ids.push(id);
      }

      const newFilters = replaceOrAdd(state.filters, filter);

      return {
        ...state,
        filters: newFilters.filter(
          item => item.id || (item.ids && item.ids.length)
        ),
      };
    }
    case 'FILTER_REMOVE':
      return {
        ...state,
        filters: state.filters.filter(
          filter => filter.property !== action.property
        ),
      };
    case 'AGGREGATION_UPDATE': {
      const aggregations = (state && state.aggregations) || [];
      return {
        ...state,
        aggregations: mergeIntoQueryArray(
          aggregations,
          action.aggregation,
          'id'
        ),
      };
    }
    case 'CHANGE_SORT': {
      return {
        ...state,
        sort: action.sort,
      };
    }
    default: {
      return state;
    }
  }
};

ProductCollection.variables = ({ id, slug }) => ({
  id,
  slug,
});

ProductCollection.stateToVariables = state => ({
  query: {
    url: state.url,
    filters: !state.url && state.filters ? state.filters : undefined,
    aggregations: state.aggregations,
    sort: state.sort,
    offset: state.offset,
  },
});

export default ProductCollection;
